"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { fetchFeatureFlags } from "@/lib/helpers";
import { FeatureFlag } from "@/types/feature-flags";

interface FeatureFlagsContextType {
  flags: Record<string, FeatureFlag>;
  loading: boolean;
  isEnabled: (key: string) => boolean;
  getMetadata: (key: string) => Record<string, unknown> | null;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined
);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider"
    );
  }
  return context;
};

/**
 * Flag access for components that render both inside and outside the provider.
 *
 * `FeatureFlagsProvider` is mounted only in the (dashboard) layout, so shared
 * chrome like the language switcher — which also renders on the (auth) sign-in
 * page — cannot use `useFeatureFlags()` without throwing there.
 *
 * Outside the provider `isEnabled` is always `false` and `loading` is `false`:
 * there is no fetch to wait for, so consumers see a settled "flag off" rather
 * than a permanent loading state. Gate on `loading` when a false negative
 * matters; flags are never enabled on unauthenticated surfaces.
 */
export const useOptionalFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  return context ?? EMPTY_FEATURE_FLAGS;
};

const EMPTY_FEATURE_FLAGS: FeatureFlagsContextType = {
  flags: {},
  loading: false,
  isEnabled: () => false,
  getMetadata: () => null
};

export const FeatureFlagsProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [flags, setFlags] = useState<Record<string, FeatureFlag>>({});
  // Starts true so consumers can wait for the first fetch before acting on
  // flags. `isEnabled` returns false until flags load, so gating on this avoids
  // false negatives (e.g. routing a cards account before its flag arrives).
  const [loading, setLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    try {
      const data = await fetchFeatureFlags();
      // `fetchFeatureFlags` is a server action, so its declared return type is
      // not a runtime guarantee: when the Auth0 session can't be refreshed it
      // redirects to /api/auth/logout and the action resolves to `undefined` on
      // the client (a 204 does the same). Committing that would make every
      // isEnabled() call throw and take down the dashboard layout mid-logout.
      setFlags(data ?? {});
    } catch (err) {
      console.error("Failed to load user feature flags:", err);
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Optional-chain the map itself, not just the flag: these accessors run
  // inside the dashboard layout (CapitalOS provider, sidebar, promo), and an
  // error thrown there bypasses (dashboard)/error.tsx and blanks the whole app.
  const isEnabled = useCallback(
    (key: string) => flags?.[key]?.enabled ?? false,
    [flags]
  );
  const getMetadata = useCallback(
    (key: string) => flags?.[key]?.metadata ?? null,
    [flags]
  );

  const value = useMemo(
    () => ({ flags, loading, isEnabled, getMetadata }),
    [flags, loading, isEnabled, getMetadata]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
